/*
 * Free-shipping progress bar.
 *
 * Shows how close the cart is to the $75 threshold, and celebrates when it's
 * crossed. Injected into both the cart drawer and the cart page.
 *
 * Dawn re-renders the drawer's HTML on every cart change, which wipes anything
 * added to it — so this re-injects after each update and watches the DOM rather
 * than assuming its node survives.
 */
(() => {
  const THRESHOLD = 7500; // cents — keep in step with scripts/setup-free-shipping.mjs
  // `.cart__items` is a <table> in Dawn — inserting a div after it lands in a
  // layout context with no width, so the bar rendered 0px wide. Mount above the
  // cart footer instead, which is a normal block element.
  const MOUNTS = [
    { sel: '.drawer__inner .drawer__header', at: 'afterend', drawer: true },  // cart drawer
    { sel: '.cart__footer', at: 'beforebegin' },                // cart page
    { sel: '#main-cart-footer', at: 'beforebegin' },
  ];

  const money = (cents) =>
    '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');

  function render(total) {
    const remaining = Math.max(0, THRESHOLD - total);
    const pct = Math.min(100, (total / THRESHOLD) * 100);
    const done = remaining === 0;

    // Two-part header — a quiet label on the left, the number that actually
    // motivates on the right — then the track. The amount is the message, so
    // it gets the size and the colour; everything else recedes.
    return `
      <div class="gbf-ship${done ? ' is-done' : ''}" data-gbf-ship>
        <div class="gbf-ship__head">
          <span class="gbf-ship__label">${done ? 'Free shipping' : 'Free shipping at $75'}</span>
          <span class="gbf-ship__value">${
            done
              ? '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8.5l4 4L14 4"/></svg>Unlocked'
              : `${money(remaining)} to go`
          }</span>
        </div>
        <div class="gbf-ship__track" role="progressbar" aria-valuemin="0" aria-valuemax="100"
             aria-valuenow="${Math.round(pct)}"
             aria-label="${done ? 'Free shipping unlocked' : `${money(remaining)} away from free shipping`}">
          <span class="gbf-ship__fill" style="width:${pct}%"></span>
        </div>
        ${
          done
            ? ''
            : // Naming the gap without a way to close it is a dead end. Point at
              // the cheap add-ons when they're nearly there, the full shop when
              // they aren't.
              `<a class="gbf-ship__cta" href="${remaining <= 2500 ? '/collections/accessories' : '/collections/all'}">
                 ${remaining <= 2500 ? 'Add something small' : 'Keep shopping'}
                 <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
               </a>`
        }
      </div>`;
  }

  async function update() {
    let cart;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      cart = await res.json();
    } catch {
      return; // a failed poll should leave the page exactly as it was
    }
    if (!cart || typeof cart.total_price !== 'number') return;

    const html = render(cart.total_price);

    // One bar per context. `.cart__footer` and `#main-cart-footer` both match
    // the same region in Dawn, so mounting into each rendered it twice.
    const drawer = MOUNTS.find((m) => m.drawer && document.querySelector(m.sel));
    const page = MOUNTS.find((m) => !m.drawer && document.querySelector(m.sel));

    for (const mount of [drawer, page]) {
      if (!mount) continue;
      const host = document.querySelector(mount.sel);
      const sibling = mount.at === 'beforebegin' ? host.previousElementSibling : host.nextElementSibling;
      if (sibling && sibling.matches('[data-gbf-ship]')) {
        sibling.outerHTML = html;
      } else if (cart.item_count > 0) {
        host.insertAdjacentHTML(mount.at, html);
      }
    }

    // Anything left over from an earlier render (Dawn swaps section HTML on
    // update) would otherwise stack up down the page.
    const keep = new Set();
    for (const mount of [drawer, page]) {
      if (!mount) continue;
      const host = document.querySelector(mount.sel);
      const sib = mount.at === 'beforebegin' ? host.previousElementSibling : host.nextElementSibling;
      if (sib && sib.matches('[data-gbf-ship]')) keep.add(sib);
    }
    document.querySelectorAll('[data-gbf-ship]').forEach((el) => { if (!keep.has(el)) el.remove(); });
  }

  // Cart mutations all go through these endpoints; piggyback rather than
  // depending on Dawn's internal pub/sub, which differs between versions.
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url ?? '';
    const p = origFetch.apply(this, args);
    if (/\/cart\/(add|change|update|clear)/.test(url)) p.then(() => setTimeout(update, 120)).catch(() => {});
    return p;
  };

  // The drawer is injected after page load, so wait for it to appear.
  const observer = new MutationObserver(() => {
    if (document.querySelector(MOUNTS.map((m) => m.sel).join(',')) && !document.querySelector('[data-gbf-ship]')) update();
  });

  const start = () => {
    update();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
